import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Outlet,
  Scripts,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CookieBanner } from "@/components/CookieBanner";
import { reportVibeError } from "@/lib/vibe-error-reporting";
import appCss from "../styles.css?url";

const delayedThirdPartyScripts = `(function () {
  var scriptsLoaded = false;
  function loadScripts() {
    if (scriptsLoaded) return;
    scriptsLoaded = true;
    var gtmScript = document.createElement("script");
    gtmScript.async = true;
    gtmScript.src = "https://www.googletagmanager.com/gtag/js?id=G-WXHRDVD5WF";
    document.body.appendChild(gtmScript);
    window.dataLayer = window.dataLayer || [];
    function gtag() { dataLayer.push(arguments); }
    gtag("js", new Date());
    gtag("config", "G-WXHRDVD5WF");
    var doScript = document.createElement("script");
    doScript.setAttribute("nowprocket", "");
    doScript.setAttribute("nitro-exclude", "");
    doScript.type = "text/javascript";
    doScript.id = "sa-dynamic-optimization";
    doScript.dataset.uuid = "3dea4b24-aed1-4cef-b87a-febb5d744003";
    doScript.src = "data:text/javascript;base64,dmFyIHNjcmlwdCA9IGRvY3VtZW50LmNyZWF0ZUVsZW1lbnQoInNjcmlwdCIpO3NjcmlwdC5zZXRBdHRyaWJ1dGUoIm5vd3Byb2NrZXQiLCAiIik7c2NyaXB0LnNldEF0dHJpYnV0ZSgibml0cm8tZXhjbHVkZSIsICIiKTtzY3JpcHQuc3JjID0gImh0dHBzOi8vZGFzaGJvYXJkLnNlYXJjaGF0bGFzLmNvbS9zY3JpcHRzL2R5bmFtaWNfb3B0aW1pemF0aW9uLmpzIjtzY3JpcHQuZGF0YXNldC51dWlkID0gIjNkZWE0YjI0LWFlZDEtNGNlZi1iODdhLWZlYmI1ZDc0NDAwMyI7c2NyaXB0LmlkID0gInNhLWR5bmFtaWMtb3B0aW1pemF0aW9uLWxvYWRlciI7ZG9jdW1lbnQuaGVhZC5hcHBlbmRDaGlsZChzY3JpcHQpOw==";
    document.body.appendChild(doScript);
    var rwScript = document.createElement("script");
    rwScript.type = "text/javascript";
    rwScript.src = "https://reputationhub.site/reputation/assets/review-widget.js";
    document.body.appendChild(rwScript);
    var chatScript = document.createElement("script");
    chatScript.src = "https://widgets.leadconnectorhq.com/loader.js";
    chatScript.dataset.resourcesUrl = "https://widgets.leadconnectorhq.com/chat-widget/loader.js";
    chatScript.dataset.widgetId = "69d474dca3eb88148dbeedf9";
    document.body.appendChild(chatScript);
  }
  window.addEventListener("scroll", loadScripts, { once: true, passive: true });
  window.addEventListener("click", loadScripts, { once: true, passive: true });
  window.addEventListener("touchstart", loadScripts, { once: true, passive: true });
  setTimeout(loadScripts, 5000);
})();`;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        {
          httpEquiv: "Content-Security-Policy",
          content:
            "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' data: https://www.googletagmanager.com https://seo-tools.leadconnectorhq.com https://reputationhub.site https://widgets.leadconnectorhq.com https://stcdn.leadconnectorhq.com https://services.leadconnectorhq.com https://dashboard.searchatlas.com; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://fonts.bunny.net https://stcdn.leadconnectorhq.com; font-src 'self' https://fonts.gstatic.com data:; img-src 'self' data: blob: https://*; connect-src 'self' https://* wss://*; frame-src 'self' https://*;",
        },
        { name: "author", content: "The Outpost Creative" },
        { title: "The Outpost Creative" },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        {
          rel: "icon",
          type: "image/png",
          href: "https://assets.cdn.filesafe.space/rizeckkMPBYZT9pK9UjX/media/69d478f43d829c73b28c9505.png",
        },
        { rel: "preconnect", href: "https://vibe.filesafe.space" },
        { rel: "preconnect", href: "https://assets.cdn.filesafe.space" },
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=Share+Tech+Mono&display=swap",
        },
      ],
      scripts: [{ children: delayedThirdPartyScripts }],
    }),
    shellComponent: RootShell,
    component: RootComponent,
    notFoundComponent: NotFoundComponent,
    errorComponent: RootErrorComponent,
  },
);

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <CookieBanner />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function NotFoundComponent() {
  return (
    <main className="grid min-h-screen place-items-center bg-background px-6 text-foreground">
      <div className="max-w-lg text-center">
        <p className="font-mono text-sm uppercase tracking-[0.24em] text-primary">
          404
        </p>
        <h1 className="mt-4 text-4xl font-bold">This page is off the map</h1>
        <p className="mt-4 text-muted-foreground">
          The page you requested could not be found.
        </p>
        <Link
          to="/"
          className="mt-8 inline-flex rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground"
        >
          Return to Base
        </Link>
      </div>
    </main>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  useEffect(() => {
    console.error(error);
    reportVibeError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <main className="grid min-h-screen place-items-center bg-background px-6 text-foreground">
      <div className="max-w-lg text-center">
        <h1 className="text-4xl font-bold">This page didn't load</h1>
        <p className="mt-4 text-muted-foreground">
          Something went wrong on our end. Try again or return home.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <button
            className="rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <Link
            to="/"
            className="rounded-md border border-border px-5 py-3 font-semibold"
          >
            Go home
          </Link>
        </div>
      </div>
    </main>
  );
}
